// The priority action inbox (SEP-228 §37).
//
// A priority action names one client and one thing to do. It appears on the
// home landing page of the team that must act. It stays visible until the work
// behind it lands, or until a person clears it.
//
// The audience is the part that matters. An action reaches Ops, or Compliance,
// or the desk pair of one client. The reader never names their own audience:
// the server derives it from the identity of the caller. There is therefore no
// audience field, no user field and no team field on any request here, and
// there can never be one.

import { z } from 'zod';

/**
 * Who must act.
 *
 * `client_desk` is the pair that works one client: the investment manager who
 * owns the client, and the assistant who acts for that manager. The firm
 * directory resolves the pair, so no raiser names an assistant by hand.
 */
export const priorityActionAudienceSchema = z.enum(['ops', 'comply', 'client_desk']);
export type PriorityActionAudience = z.infer<typeof priorityActionAudienceSchema>;

/**
 * The record an action is about. The personal AML tier raises against a
 * Contact. The entity tier raises against a Suitability Record, which anchors
 * the Entity AML Assessment. The inbox itself holds nothing AML specific, so a
 * later caller adds its own kind of subject here.
 */
export const priorityActionSubjectKindSchema = z.enum([
    'contact',
    'suitability_record',
    /** SEP-264 — a PA trade request in the Compliance review queue. */
    'pa_trade_request',
    /** SEP-288 — the fact find of one person. The route opens its New Fact Find screen. */
    'fact_find',
    /** SEP-293 — the Internal Suitability Assessment of one client. The subject id is the client id. */
    'suitability_assessment',
    /**
     * SEP-360 — one corporate action. The subject id is the CoacAction id. The
     * route opens the action on the Desk corporate actions screen.
     */
    'coac_action',
    /**
     * SEP-359 — one transfer ticket. The subject id is the TransferRequest id.
     * The Desk opens the ticket page; Operations opens the ticket on its list.
     */
    'transfer_request',
]);
export type PriorityActionSubjectKind = z.infer<typeof priorityActionSubjectKindSchema>;

/**
 * The family of an action. One kind raises at most one open action for one
 * record, so a second raise updates the first.
 *
 * The kinds below are the SEP-228 flows of §37, and the ongoing screening
 * alert of §34. Moderate and High share
 * `aml_review_required`, because they are one flow with two titles: a Moderate
 * assessment that a re-run turns High must move the open action, not add a
 * second one.
 */
export const priorityActionKindSchema = z.enum([
    /** §5.3 — SmartSearch cannot identify the person. Compliance must review. */
    'aml_identity_compliance_review',
    /** §6 Option 2 — Compliance wants more proof of identity and address. */
    'aml_identity_evidence_request',
    /** §6 Option 1 and §7 — Compliance verified the identity. Ops continues. */
    'aml_identity_verified',
    /** §23 and §24 — the assessment returned Moderate or High. */
    'aml_review_required',
    /** §25 — the answers trigger enhanced due diligence. */
    'aml_edd_required',
    /** §28 — Compliance approved the assessment. Ops continues. */
    'aml_decision_approved',
    /** §33 — the next AML review falls due in 30 days. */
    'aml_review_due',
    /** §34 — an ongoing screening alert needs a compliance review. */
    'aml_ongoing_screening_alert',
    /** SEP-264 — a PA trade request needs a Compliance review. */
    'pa_request_pending_review',
    /** SEP-264 — an employee tried to trade a security on the Restricted List. */
    'pa_restricted_attempt',
    /** SEP-264 — the employee answered an information request. */
    'pa_information_provided',
    /** SEP-264 — the employee commented on a request that waits on Compliance. */
    'pa_employee_comment',
    /** SEP-288 §3 — the fact find reaches 18 months within one month. Create a new one. */
    'suitability_refresh_due',
    /** SEP-288 §3 — a reminder to the client: 10 and 20 business days after the issue. */
    'suitability_refresh_reminder',
    /** SEP-288 §3 — no response 30 calendar days after the issue. Compliance follows up. */
    'suitability_refresh_escalated',
    /** SEP-288 §5 — Compliance approved Outcome B. A new mandate is required. */
    'suitability_mandate_required',
    /** SEP-293 §8 — the peer reviewer escalated the flags of a review to Compliance. */
    'suitability_flags_escalated',
    /** SEP-293 §8 — a client who relies on the portfolio holds a higher-risk profile. */
    'suitability_reliance_compliance_review',
    /** SEP-293 §7 — a source of an approved review changed after the final approval. */
    'suitability_source_changed_after_approval',
    /**
     * SEP-360 — 12pm on the internal deadline date: the responses close at
     * 4pm, and the IM has a portfolio with no response. It reaches each IM
     * with an open portfolio, and their assistant.
     */
    'coac_response_deadline_reminder',
    /**
     * SEP-359 — a Full - In Cash Transfer OUT of a portfolio that holds
     * securities. The IM confirms that the sell orders are placed. The
     * confirmation clears it.
     */
    'transfer_securities_sale_confirmation',
    /**
     * SEP-359 — the IM answered No to "Have all outstanding management fees
     * been deducted prior to the transfer?". Operations follows up with the IM.
     * It never stops the transfer. Operations clears it.
     */
    'transfer_management_fees_follow_up',
]);
export type PriorityActionKind = z.infer<typeof priorityActionKindSchema>;

/**
 * One open action, as its reader sees it.
 *
 * `route` is a presentation value. The controller builds it from the subject
 * and from the audience of the row, because each view reaches a Contact under
 * its own path prefix. It is not stored.
 */
export const priorityActionSchema = z.object({
    id: z.string(),
    kind: priorityActionKindSchema,
    audience: priorityActionAudienceSchema,
    subjectKind: priorityActionSubjectKindSchema,
    subjectId: z.string(),
    /** The client name, as the raiser read it. A re-raise refreshes it. */
    subjectLabel: z.string(),
    /** The work to do, in one line. */
    title: z.string(),
    /** More detail, or null when the title says everything. */
    body: z.string().nullable(),
    /** The path that opens the record. One press opens the contact. */
    route: z.string(),
    raisedAt: z.string(),
    updatedAt: z.string(),
});
export type PriorityAction = z.infer<typeof priorityActionSchema>;

export const priorityActionsResponseSchema = z.object({
    actions: z.array(priorityActionSchema),
});
export type PriorityActionsResponse = z.infer<typeof priorityActionsResponseSchema>;

export const clearPriorityActionRequestSchema = z.object({
    id: z.string().min(1),
});
export type ClearPriorityActionRequest = z.infer<typeof clearPriorityActionRequestSchema>;
